import { useState } from 'react';
import type { InputHTMLAttributes } from 'react';

/**
 * A clamped numeric input you can actually TYPE in. Binding a store value
 * with an eagerly-clamping setter straight to a controlled <input> makes
 * typing impossible: entering "96" into a tempo field clamped to 40..320
 * goes "9" → clamped to 40 → "6" appends → "406" → clamped to 320. While
 * focused, the raw text lives in a local draft; values that are already
 * valid and in range apply live (so sliders/readouts track), and blur or
 * Enter commits whatever was typed through the setter's own clamp.
 */
export function NumberField({
  value,
  onCommit,
  min,
  max,
  ...rest
}: {
  value: number;
  onCommit: (n: number) => void;
  min?: number;
  max?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'min' | 'max' | 'type'>) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      type="number"
      min={min}
      max={max}
      value={draft ?? String(value)}
      onFocus={() => setDraft(String(value))}
      onChange={(e) => {
        setDraft(e.target.value);
        const n = Number(e.target.value);
        const inRange =
          e.target.value !== '' &&
          Number.isFinite(n) &&
          (min === undefined || n >= min) &&
          (max === undefined || n <= max);
        if (inRange) onCommit(n);
      }}
      onBlur={(e) => {
        const n = Number(e.target.value);
        if (e.target.value !== '' && Number.isFinite(n)) onCommit(n);
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      {...rest}
    />
  );
}
